import { apiFetch } from "./client";

export type UserSummary = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  locale: string;
  companyId: string | null;
  companyName: string | null;
  impInternal: boolean;
  forecastsEnabled: boolean;
  phone: string | null;
  avatarUrl: string | null;
  roles: string[];
};

export type TwoFactorStatus = {
  enabled: boolean;
  required: boolean;
  setupRequired: boolean;
  graceUntil: string | null;
};

export type MeResponse = {
  user: UserSummary;
  permissions: string[];
  twoFactor: TwoFactorStatus;
};

export type InvitationInfo = {
  email: string;
  companyId: string | null;
  companyName: string | null;
  roles: string[];
  expiresAt: string;
};

export type LoginResponse = UserSummary | { mfaRequired: true };

export const authApi = {
  login: (email: string, password: string, rememberMe?: boolean, totpCode?: string) =>
    apiFetch<LoginResponse>("/auth/login", {
      method: "POST",
      body: { email, password, rememberMe: !!rememberMe, totpCode: totpCode ?? null },
      skipRefresh: true,
    }),

  logout: () =>
    apiFetch<void>("/auth/logout", { method: "POST", skipRefresh: true }),

  refresh: () =>
    apiFetch<UserSummary>("/auth/refresh", { method: "POST", skipRefresh: true }),

  me: () => apiFetch<MeResponse>("/auth/me"),

  forgotPassword: (email: string) =>
    apiFetch<void>("/auth/forgot-password", {
      method: "POST",
      body: { email },
      skipRefresh: true,
    }),

  resetPassword: (token: string, newPassword: string) =>
    apiFetch<void>("/auth/reset-password", {
      method: "POST",
      body: { token, newPassword },
      skipRefresh: true,
    }),

  inspectInvitation: (token: string) =>
    apiFetch<InvitationInfo>(`/auth/invitations/${encodeURIComponent(token)}`, {
      skipRefresh: true,
    }),

  acceptInvitation: (
    token: string,
    body: { password: string; firstName: string; lastName: string; locale?: string; phone?: string },
  ) =>
    apiFetch<UserSummary>(`/auth/invitations/${encodeURIComponent(token)}/accept`, {
      method: "POST",
      body,
      skipRefresh: true,
    }),

  updateLocale: (locale: string) =>
    apiFetch<void>("/me/locale", { method: "PATCH", body: { locale } }),

  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch<void>("/me/password", {
      method: "POST",
      body: { currentPassword, newPassword },
    }),

  listSessions: () => apiFetch<SessionView[]>("/me/sessions"),

  revokeSession: (id: string) =>
    apiFetch<void>(`/me/sessions/${encodeURIComponent(id)}`, { method: "DELETE" }),

  presignAvatar: (contentType: string, sizeBytes: number) =>
    apiFetch<AvatarPresignResponse>("/me/avatar/presign", {
      method: "POST",
      body: { contentType, sizeBytes },
    }),

  confirmAvatar: (objectKey: string) =>
    apiFetch<{ avatarUrl: string }>("/me/avatar/confirm", {
      method: "POST",
      body: { objectKey },
    }),

  deleteAvatar: () =>
    apiFetch<void>("/me/avatar", { method: "DELETE" }),
};

export type AvatarPresignResponse = {
  url: string;
  objectKey: string;
};

export type SessionView = {
  id: string;
  userAgent: string | null;
  ipAddress: string | null;
  issuedAt: string;
  lastUsedAt: string | null;
  current: boolean;
};
